"use client";

// Vio, drawn as vector rather than served as a bitmap.
//
// WHY THIS REPLACED THE PNG. The character used to be `public/vio-robot.png` —
// the mobile app's art, cut off its badge (scripts/cut-vio.py) and hue-rotated
// from purple to the site copper (scripts/recolour-vio.py). That was the right
// call for a mascot that only had to float and be dropped on, and the two
// scripts are still the source of truth for the character's colour and
// silhouette. But a bitmap face cannot look at anything. Eyes that follow the
// cursor, a head that leans toward the paragraph you just selected, an antenna
// that reacts, and eight distinguishable expressions are all the same
// requirement underneath: the parts have to be addressable. So the artwork is
// rebuilt as SVG, and the PNG stays in `public/` as the visual reference the
// rebuild is measured against.
//
// IT IS A TRACING, NOT A REDESIGN. Every coordinate below was measured off
// `vio-robot.png` at its native 266x274 — visor x40-226 y83-191, eyes at
// x85.5 and x180.5 on y134, mouth x116-150 y156-167, antenna ball r13 at
// (132,16), side pods y108-172, waist pinch at y210 — and the two silhouette
// paths are superellipses fitted to the PNG's own row widths. The colours are
// sampled pixels, not picked ones: shell #F5F0EA, visor #180805, eyes #D99354,
// ball #F6F6D1, collar #CA8969. The viewBox is padded to -30/-22 so the arms
// have somewhere to open into; the character still occupies 0-266 x 0-274.
//
// THIS IS NOT `vio/VioAvatar` OR `vio/VioFigure`. Those two are the flat
// copper roundel and the standing illustration used inside the product UI —
// small, static, and drawn to their own geometry. This one is the floating
// character on the marketing site, and it is the only one that has states.
//
// WHAT THIS FILE DOES AND DOES NOT DECIDE. It draws every variant of every
// part, once, and lets `data-state` choose which are visible — so switching
// expression is a CSS opacity change on an already-mounted tree rather than a
// React re-render, and the transitions between states are interpolations
// rather than swaps. Which state Vio is in is VioFab's decision; what each
// state is allowed to mean is `states.ts`; how each one looks is the `.vio-svg`
// block in globals.css, next to the rest of this site's styling.
//
// NO SVG FILTERS, DELIBERATELY. Every glow here is a radial-gradient shape.
// `feGaussianBlur` on a element that moves every frame (the pupils track the
// cursor) re-rasterises the filter region each frame; a gradient is a plain
// paint the compositor already knows how to move. The look is the same at
// 104px and the cost is not.

import { useId } from "react";

import type { VioState } from "./states";

type Props = {
  state: VioState;
  /** Rendered CSS width. The viewBox keeps the character ~80% of this. */
  size?: number;
  /**
   * Close the eyes into the reference arch.
   *
   * Set when there is nothing to look at — a coarse pointer (a phone has no
   * cursor at all) or a pointer that has not moved for a few seconds. Open
   * eyes tracking a cursor that is not there read as a stare; the arch is
   * both the resting pose and the exact expression the source art wears, so
   * Vio's default appearance stays the one people recognise.
   */
  rest?: boolean;
  className?: string;
};

// ── Measured geometry, named so the drawing below reads as the reference ──
const VISOR = { x: 40, y: 83, w: 186, h: 108, r: 46 };
const EYE_L = 85; // measured 85.5
const EYE_R = 181; // measured 180.5
const EYE_Y = 134;
const MOUTH_Y = 162;

// Silhouettes fitted to the PNG's per-row widths (superellipse, different
// exponent above and below the waist — Vio's head is fuller at the chin than
// a symmetric curve gives, and the body is a flattened pill, not an ellipse).
const SHELL_D =
  "M133 38c12 0 24 1 36 4c11 3 22 7 32 12c10 5 18 12 25 19c7 7 13 15 16 23" +
  "c4 8 5 14 6 26c0 12 -1 37 -3 48c-2 11 -5 13 -9 18c-4 5 -9 8 -15 11" +
  "c-7 3 -10 5 -24 7c-15 1 -43 2 -64 2c-21 0 -49 -1 -64 -2c-15 -1 -18 -4 -24 -7" +
  "c-7 -3 -11 -6 -15 -11c-4 -5 -7 -7 -9 -18c-2 -11 -3 -36 -3 -48c0 -12 2 -18 6 -26" +
  "c4 -8 9 -16 16 -23c7 -7 16 -13 25 -19c10 -5 21 -9 32 -12c11 -3 24 -4 36 -4z";

const BODY_D =
  "M133 210c18 0 41 0 53 2c13 1 16 3 22 5c6 2 9 4 12 9c3 5 4 15 4 21" +
  "c0 6 -1 13 -4 17c-2 4 -6 5 -11 6c-5 2 -7 3 -20 3c-13 1 -38 1 -56 1" +
  "c-19 0 -44 -0 -56 -1c-13 -1 -15 -2 -20 -3c-5 -2 -9 -2 -11 -6c-2 -4 -4 -11 -4 -17" +
  "c0 -6 1 -16 4 -21c3 -5 6 -6 12 -9c6 -2 9 -4 22 -5c13 -1 36 -2 53 -2z";

/** One eye. Both variants are drawn; CSS crossfades them per state. */
function Eye({ x, side, halo }: { x: number; side: "l" | "r"; halo: string }) {
  return (
    <g className={`v-eye v-eye-${side}`} transform={`translate(${x} ${EYE_Y})`}>
      {/* Behind everything, so the glow reads as light spilling onto the visor
          rather than as a ring drawn around the eye. */}
      <circle className="v-eye-halo" fill={`url(#${halo})`} r="30" />

      {/* The reference expression: a closed, contented arch. This is what the
          PNG shows, so it is what Vio wears at rest and at every happy beat. */}
      <path
        className="v-eye-arc"
        d="M-16 6 Q0 -11 16 6"
        fill="none"
        strokeLinecap="round"
        strokeWidth="10"
      />

      {/* The open eye, which is the one that can look at you. Only ever
          visible in states where tracking is live (states.ts::tracksCursor).

          Two nested groups because the blink and the state shape are both
          scaleY on the same axis and the outer one would silently win: a CSS
          animation outranks a normal declaration, so a `transform` set by a
          state rule on a blinking element never applies at all. The outer
          group blinks, the inner one holds the shape. */}
      <g className="v-eye-open">
        <g className="v-eye-shape">
          <ellipse className="v-eye-lens" rx="16" ry="17" />
          <ellipse className="v-pupil" rx="7.5" ry="8" />
          {/* A fixed catchlight, NOT tracked. It is a reflection of the room,
              not of where Vio is looking; moving it with the pupil is the
              classic tell that a character's eyes are painted on. */}
          <circle className="v-eye-spec" cx="-5" cy="-6.5" r="2.6" />
        </g>
      </g>

      {/* Brows do most of the work in warning and error — they are legible at
          104px where a mouth curve is three pixels of difference. Drawn level
          and rotated per state, so "concerned" and "stern" are one sign flip
          rather than two more paths to keep in step. */}
      <path
        className="v-brow"
        d="M-17 -27 Q0 -30 17 -27"
        fill="none"
        strokeLinecap="round"
        strokeWidth="5"
      />
    </g>
  );
}

export default function VioCharacter({
  state,
  size = 128,
  rest = false,
  className,
}: Props) {
  // Unique gradient ids per instance. The moment two Vios share a page —
  // the floating one and a second rendered inline — duplicate ids would make
  // both paint from whichever <defs> the browser resolved first. React 19's
  // useId contains characters that are not valid in a URL fragment, so it is
  // stripped rather than used raw.
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const id = (n: string) => `${uid}-${n}`;

  return (
    <svg
      // The character is decorative; everything it means is said in text by
      // the bubble's aria-live region and by VioFab's link label.
      aria-hidden="true"
      className={`vio-svg${className ? ` ${className}` : ""}`}
      data-rest={rest ? "1" : undefined}
      data-state={state}
      focusable="false"
      height={Math.round((size * 320) / 326)}
      viewBox="-30 -22 326 320"
      width={size}
    >
      <defs>
        {/* Shell: white with a warm rim, lit from the upper left. The focal
            point off-centre is what keeps it reading as a sphere. */}
        <radialGradient cx="35%" cy="20%" id={id("shell")} r="90%">
          <stop offset="0" stopColor="#FFFEFD" />
          <stop offset=".34" stopColor="#FAF4EE" />
          <stop offset=".60" stopColor="#F0DCCE" />
          <stop offset=".84" stopColor="#DFAB8B" />
          <stop offset="1" stopColor="#C4764B" />
        </radialGradient>

        {/* Ambient occlusion under the chin, where the helmet turns away from
            the light and meets the collar. Painted as a gradient the shell is
            filled with a second time rather than as a drawn shadow, so it
            follows the silhouette exactly. */}
        <radialGradient cx="50%" cy="104%" id={id("shellAo")} r="62%">
          <stop offset="0" stopColor="#8E4A22" stopOpacity=".46" />
          <stop offset=".55" stopColor="#B0663A" stopOpacity=".14" />
          <stop offset="1" stopColor="#B0663A" stopOpacity="0" />
        </radialGradient>

        {/* Rim light. The reference's helmet has a bright lit edge along the
            upper left and a dark one on the lower right; without it the shell
            reads as a flat white blob however good the fill gradient is. */}
        <linearGradient id={id("rim")} x1=".08" x2=".78" y1=".05" y2=".95">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity=".95" />
          <stop offset=".34" stopColor="#FFF3E9" stopOpacity=".45" />
          <stop offset=".62" stopColor="#C98A5F" stopOpacity=".22" />
          <stop offset="1" stopColor="#8E4A22" stopOpacity=".42" />
        </linearGradient>

        {/* The pearl ring around the glass — lit at the top-left, warm and
            shadowed at the bottom-right, the same key light as the shell. */}
        <linearGradient id={id("bezel")} x1=".15" x2=".8" y1="0" y2="1">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity=".92" />
          <stop offset=".45" stopColor="#F6DFCD" stopOpacity=".62" />
          <stop offset="1" stopColor="#C1794E" stopOpacity=".55" />
        </linearGradient>

        {/* Specular sweep across the top of the helmet. */}
        <linearGradient id={id("gloss")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity=".85" />
          <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>

        <radialGradient cx="50%" cy="16%" id={id("visor")} r="94%">
          <stop offset="0" stopColor="#4B2C27" />
          <stop offset=".46" stopColor="#25100B" />
          <stop offset="1" stopColor="#130604" />
        </radialGradient>

        {/* The glass highlight that sits ON the visor, above the face. */}
        <linearGradient id={id("visorGloss")} x1=".1" x2=".5" y1="0" y2="1">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity=".20" />
          <stop offset=".55" stopColor="#FFFFFF" stopOpacity=".03" />
          <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>

        <linearGradient id={id("pod")} x1=".15" x2=".85" y1="0" y2="1">
          <stop offset="0" stopColor="#F2DACB" />
          <stop offset=".48" stopColor="#CE8F6C" />
          <stop offset="1" stopColor="#A2532F" />
        </linearGradient>

        <linearGradient id={id("body")} x1=".2" x2=".8" y1="-.1" y2="1.05">
          <stop offset="0" stopColor="#FEFAF4" />
          <stop offset=".34" stopColor="#F6E7D4" />
          <stop offset=".68" stopColor="#E2BC9B" />
          <stop offset="1" stopColor="#B06A42" />
        </linearGradient>

        <linearGradient id={id("collar")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#EAC1A3" />
          <stop offset="1" stopColor="#B26A41" />
        </linearGradient>

        <radialGradient cx="36%" cy="30%" id={id("ball")} r="72%">
          <stop offset="0" stopColor="#FFFFF7" />
          <stop offset=".55" stopColor="#F6F0BE" />
          <stop offset="1" stopColor="#DFA457" />
        </radialGradient>

        {/* Glows. `--vio-glow` is re-pointed per state in globals.css, which is
            how warning goes amber and error goes red without a second tree. */}
        <radialGradient id={id("ballGlow")}>
          <stop offset="0" stopColor="var(--vio-glow, #E8B45F)" stopOpacity=".55" />
          <stop offset=".5" stopColor="var(--vio-glow, #E8B45F)" stopOpacity=".16" />
          <stop offset="1" stopColor="var(--vio-glow, #E8B45F)" stopOpacity="0" />
        </radialGradient>

        <radialGradient id={id("halo")}>
          <stop offset="0" stopColor="var(--vio-glow, #E09A57)" stopOpacity=".62" />
          <stop offset=".42" stopColor="var(--vio-glow, #E09A57)" stopOpacity=".24" />
          <stop offset=".72" stopColor="var(--vio-glow, #E09A57)" stopOpacity=".07" />
          <stop offset="1" stopColor="var(--vio-glow, #E09A57)" stopOpacity="0" />
        </radialGradient>

        {/* The aura only earns its place while Vio is doing something — armed,
            scanning, or reporting. At rest it is fully transparent. */}
        <radialGradient id={id("aura")}>
          <stop offset=".38" stopColor="var(--vio-glow, #B86E2C)" stopOpacity=".22" />
          <stop offset=".72" stopColor="var(--vio-glow, #B86E2C)" stopOpacity=".07" />
          <stop offset="1" stopColor="var(--vio-glow, #B86E2C)" stopOpacity="0" />
        </radialGradient>

        {/* The scanning beam: bright core, feathered ends, so the sweep reads
            as a light passing over the visor rather than a bar sliding down. */}
        <linearGradient id={id("beam")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="var(--vio-glow, #E9C08A)" stopOpacity="0" />
          <stop offset=".5" stopColor="var(--vio-glow, #E9C08A)" stopOpacity=".85" />
          <stop offset="1" stopColor="var(--vio-glow, #E9C08A)" stopOpacity="0" />
        </linearGradient>

        {/* Contact shadow, as part of the drawing rather than a sibling div —
            so it leans and scales with the character instead of drifting. */}
        <radialGradient id={id("shadow")}>
          <stop offset="0" stopColor="#131210" stopOpacity=".42" />
          <stop offset="1" stopColor="#131210" stopOpacity="0" />
        </radialGradient>

        <clipPath id={id("visorClip")}>
          <rect
            height={VISOR.h}
            rx={VISOR.r}
            width={VISOR.w}
            x={VISOR.x}
            y={VISOR.y}
          />
        </clipPath>
      </defs>

      {/* Aura and shadow sit outside the lean group: light on the page and a
          shadow on the floor do not tilt when Vio does. */}
      <ellipse className="v-aura" cx="133" cy="152" fill={`url(#${id("aura")})`} rx="152" ry="150" />
      <ellipse className="v-shadow" cx="133" cy="284" fill={`url(#${id("shadow")})`} rx="78" ry="13" />

      <g className="v-lean">
        {/* ── Arms ──────────────────────────────────────────────────────────
            The reference's side pods, which double as arms. Pivots are set at
            the inner top of each pod so opening reads as a shoulder rotating,
            not a capsule sliding sideways. */}
        <g className="v-arm v-arm-l">
          <ellipse cx="18" cy="142" fill={`url(#${id("pod")})`} rx="24" ry="35" />
          <ellipse cx="11" cy="129" fill="#FBEDE3" opacity=".52" rx="8.5" ry="13" />
        </g>
        <g className="v-arm v-arm-r">
          <ellipse cx="248" cy="142" fill={`url(#${id("pod")})`} rx="24" ry="35" />
          <ellipse cx="255" cy="129" fill="#FBEDE3" opacity=".4" rx="8.5" ry="13" />
        </g>

        {/* ── Torso ─────────────────────────────────────────────────────── */}
        <g className="v-torso">
          <rect fill={`url(#${id("collar")})`} height="26" rx="13" width="152" x="57" y="191" />
          <path className="v-body" d={BODY_D} fill={`url(#${id("body")})`} />
          <path d={BODY_D} fill="none" stroke={`url(#${id("rim")})`} strokeWidth="2.5" />
          {/* Ambient occlusion where the body meets the collar — one soft
              stroke, which is all the reference has there. */}
          <path
            d="M64 220 Q133 232 202 220"
            fill="none"
            stroke="#B67A55"
            strokeLinecap="round"
            strokeOpacity=".30"
            strokeWidth="7"
          />
          {/* The chest mark. It is the only part that carries state colour on
              the body, and it is the largest single element that does — which
              is why success and error are readable on a phone with the bubble
              hidden. */}
          <path
            className="v-chest"
            d="M111 229 L133 261 L155 229"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="14.5"
          />
        </g>

        {/* ── Head ──────────────────────────────────────────────────────── */}
        <g className="v-head">
          <g className="v-antenna">
            <path
              className="v-stalk"
              d="M132 50 C132 41 132 35 132 28"
              fill="none"
              strokeLinecap="round"
              strokeWidth="8"
            />
            <circle className="v-ball-glow" cx="132" cy="16" fill={`url(#${id("ballGlow")})`} r="34" />
            <circle className="v-ball" cx="132" cy="15" fill={`url(#${id("ball")})`} r="14" />
            <circle cx="127" cy="10" fill="#FFFFFF" opacity=".78" r="4.4" />
          </g>

          <path className="v-shell" d={SHELL_D} fill={`url(#${id("shell")})`} />
          {/* Same path, filled again with the occlusion gradient. Two fills of
              one geometry rather than a drawn shadow that would have to be
              kept in step with the silhouette by hand. */}
          <path d={SHELL_D} fill={`url(#${id("shellAo")})`} />
          <path d={SHELL_D} fill="none" stroke={`url(#${id("rim")})`} strokeWidth="3" />
          {/* Specular sweep. Clipped to the shell by sharing its geometry
              rather than by a second clipPath. */}
          <path
            d="M133 41c-40 0-74 13-92 33c23-14 57-23 92-23s69 9 92 23c-18-20-52-33-92-33z"
            fill={`url(#${id("gloss")})`}
          />

          <g className="v-visor-g">
            <rect
              className="v-visor"
              fill={`url(#${id("visor")})`}
              height={VISOR.h}
              rx={VISOR.r}
              width={VISOR.w}
              x={VISOR.x}
              y={VISOR.y}
            />

            <g clipPath={`url(#${id("visorClip")})`}>
              {/* Scanning furniture. Present in the tree at all times and
                  transparent until the scanning state, so the beam starts the
                  instant the state changes rather than after a mount. */}
              <g className="v-scan">
                <g className="v-scan-lines">
                  <rect height="6" rx="3" width="120" x="62" y="104" />
                  <rect height="6" rx="3" width="142" x="62" y="122" />
                  <rect height="6" rx="3" width="96" x="62" y="140" />
                  <rect height="6" rx="3" width="132" x="62" y="158" />
                  <rect height="6" rx="3" width="74" x="62" y="176" />
                </g>
                {/* Parked one beam-height ABOVE the visor, so at translateY(0)
                    it is outside the clip and invisible. The sweep travels
                    visor-height + beam-height (140), which puts it fully clear
                    at both ends and never shows a half-beam at the boundary. */}
                <rect
                  className="v-beam"
                  fill={`url(#${id("beam")})`}
                  height="32"
                  width={VISOR.w + 8}
                  x={VISOR.x - 4}
                  y={VISOR.y - 32}
                />
              </g>

              <g className="v-face">
                <Eye halo={id("halo")} side="l" x={EYE_L} />
                <Eye halo={id("halo")} side="r" x={EYE_R} />

                {/* The translate lives on an OUTER group that no CSS rule
                    touches. `transform` is a presentation attribute, so it maps
                    to the CSS `transform` property — and an author stylesheet
                    beats a presentation attribute outright rather than composing
                    with it. A state rule setting `transform:scale(1.12)` on the
                    element carrying `transform="translate(133 162)"` does not
                    scale the mouth in place; it drops the translate and parks
                    the mouth in the top-left corner of the visor. */}
                <g transform={`translate(133 ${MOUTH_Y})`}>
                  <g className="v-mouth">
                    {/* Five mouths, one visible. Crossfading between drawn
                        variants keeps every shape exactly as authored — path
                        morphing would have to compromise each of them into a
                        common command structure. */}
                    <path className="v-m v-m-smile" d="M-16 -4 Q0 11 16 -4" fill="none" strokeLinecap="round" strokeWidth="9" />
                    <ellipse className="v-m v-m-o" fill="none" rx="8" ry="9.5" strokeWidth="6.5" />
                    <path className="v-m v-m-flat" d="M-11 1 L11 1" fill="none" strokeLinecap="round" strokeWidth="7.5" />
                    <path className="v-m v-m-wave" d="M-15 1 Q-7.5 -6 0 1 Q7.5 8 15 1" fill="none" strokeLinecap="round" strokeWidth="7" />
                    <path className="v-m v-m-frown" d="M-14 5 Q0 -7 14 5" fill="none" strokeLinecap="round" strokeWidth="7.5" />
                  </g>
                </g>
              </g>
            </g>

            {/* Glass, above the face — the reference's visor is reflective and
                the face sits behind it, which is why Vio reads as wearing a
                helmet rather than having a screen for a head. */}
            <path
              className="v-visor-gloss"
              d="M62 96 Q133 74 204 96 Q133 108 62 96 Z"
              fill={`url(#${id("visorGloss")})`}
            />
            <rect
              fill="none"
              height={VISOR.h}
              rx={VISOR.r}
              stroke={`url(#${id("bezel")})`}
              strokeWidth="5"
              width={VISOR.w}
              x={VISOR.x}
              y={VISOR.y}
            />
          </g>
        </g>
      </g>
    </svg>
  );
}
